// @ts-strict-ignore
import {CountType, InsightsCountType, RetentionCountType} from 'common/query-builder/query-entry-section/types';
import {
  COUNT_USERS_ONCE_TYPE,
  FUNNELS_MATH_TYPES,
  MATH_TYPE,
  MetricType,
  PRIMITIVE_MATH_TYPES,
} from 'common/types/reports/bookmark';
import {isXauMathType} from 'common/query-builder/query-builder/util';
import {ALL_MATH_TYPES} from 'common/report/constants';
import {FunnelsCountType} from 'common/report/funnels/models/constants';
import {Translatable} from 'common/i18n';
import {AggregationGroupLabel} from 'common/query-builder/types/shared-types';

import {FUNNELS_TIME_TO_CONVERSION} from '../properties-menu/constants';

export type GroupLabelParams = Omit<
  {[key in AggregationGroupLabel]: Translatable},
  AggregationGroupLabel.MetricType | AggregationGroupLabel.QueryOrigin
> & {
  [AggregationGroupLabel.MetricType]: MetricType | null;
  [AggregationGroupLabel.QueryOrigin]: `canvas` | `experiment` | `report`;
};

export const OperatorToString: Record<any, Translatable> = {
  [ALL_MATH_TYPES.TOTAL]: ({t}) => t(`query-builder.count-menu.operators.sum`),
  [ALL_MATH_TYPES.AVERAGE]: ({t}) => t(`query-builder.count-menu.operators.avg`),
  [ALL_MATH_TYPES.MEDIAN]: ({t}) => t(`query-builder.count-menu.operators.median`),
  [ALL_MATH_TYPES.MIN]: ({t}) => t(`query-builder.count-menu.operators.min`),
  [ALL_MATH_TYPES.MAX]: ({t}) => t(`query-builder.count-menu.operators.max`),
  [ALL_MATH_TYPES.P25]: `P25`,
  [ALL_MATH_TYPES.P75]: `P75`,
  [ALL_MATH_TYPES.P90]: `P90`,
  [ALL_MATH_TYPES.P99]: `P99`,
  [ALL_MATH_TYPES.UNIQUE_VALUES]: ({t}) => t(`query-builder.count-menu.operators.distinct`),
  [ALL_MATH_TYPES.HISTOGRAM]: ({t}) => t(`query-builder.count-menu.operators.distribution`),
  [ALL_MATH_TYPES.UNIQUE]: ({t}) => t(`query-builder.count-menu.operators.unique`),
  [ALL_MATH_TYPES.DAU]: ({t}) => t(`query-builder.count-menu.operators.dau`),
  [ALL_MATH_TYPES.WAU]: ({t}) => t(`query-builder.count-menu.operators.wau`),
  [ALL_MATH_TYPES.MAU]: ({t}) => t(`query-builder.count-menu.operators.mau`),
  [ALL_MATH_TYPES.CUMULATIVE_UNIQUE]: ({t}) => t(`query-builder.count-menu.operators.cumulative-users`),
  [FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE]: ({t}) => t(`query-builder.count-menu.conversion-rate-uniques`),
  [FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL]: ({t}) => t(`query-builder.count-menu.conversion-rate-total`),
  [FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION]: ({t}) => t(`query-builder.count-menu.conversion-rate-sessions`),
};

const OperatorToStringWithData: Record<any, (data?: {percentile?: number}) => Translatable> = {
  [ALL_MATH_TYPES.CUSTOM_PERCENTILE]:
    (data) =>
    ({t}) =>
      data?.percentile ? `P${data.percentile}` : t(`query-builder.count-menu.operators.custom-percentile`),
};

export function getOperatorLabel(operator: string, percentile?: number) {
  if (operator in OperatorToString) {
    return OperatorToString[operator];
  } else if (operator in OperatorToStringWithData) {
    return OperatorToStringWithData[operator]({percentile});
  }
  return null;
}

export const DefaultCountUsersOnceType = new Map<MetricType, COUNT_USERS_ONCE_TYPE>([
  [MetricType.Retention, COUNT_USERS_ONCE_TYPE.FIRST],
  [MetricType.RetentionFrequency, COUNT_USERS_ONCE_TYPE.FIRST],
  [MetricType.Funnel, COUNT_USERS_ONCE_TYPE.MULTIPLE],
  [MetricType.Event, COUNT_USERS_ONCE_TYPE.MULTIPLE],
  [MetricType.Simple, COUNT_USERS_ONCE_TYPE.MULTIPLE],
  [MetricType.CustomEvent, COUNT_USERS_ONCE_TYPE.MULTIPLE],
]);

export const COUNT_USERS_ONCE_OPTIONS = {
  [COUNT_USERS_ONCE_TYPE.MULTIPLE]: {
    label: ({defaultType}: {defaultType: COUNT_USERS_ONCE_TYPE}) => {
      return ({t}) =>
        `${t(`query-builder.count-menu.count-users-once.every-segment`)}${defaultType === COUNT_USERS_ONCE_TYPE.MULTIPLE ? ` (${t(`query-builder.default`)})` : ``}`;
    },
    selectLabel:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.count-users-once.count-every-segment`),
    description:
      ({timePeriod}) =>
      ({t}) =>
        t(`query-builder.count-menu.count-users-once.every-segment-tooltip`, {timePeriod}),
    enabledStates: [`enabled`, `disabled`],
  },
  [COUNT_USERS_ONCE_TYPE.LAST]: {
    label: ({defaultType}: {defaultType: COUNT_USERS_ONCE_TYPE}) => {
      return ({t}) =>
        `${t(`query-builder.count-menu.count-users-once.last-segment`)}${defaultType === COUNT_USERS_ONCE_TYPE.LAST ? ` (${t(`query-builder.default`)})` : ``}`;
    },
    selectLabel:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.count-users-once.count-last-segment`),
    description:
      ({timePeriod}) =>
      ({t}) =>
        t(`query-builder.count-menu.count-users-once.last-segment-tooltip`, {timePeriod}),
    enabledStates: [`enabled`, `required`],
  },
  [COUNT_USERS_ONCE_TYPE.FIRST]: {
    label: ({defaultType}: {defaultType: COUNT_USERS_ONCE_TYPE}) => {
      return ({t}) =>
        `${t(`query-builder.count-menu.count-users-once.first-segment`)}${defaultType === COUNT_USERS_ONCE_TYPE.FIRST ? ` (${t(`query-builder.default`)})` : ``}`;
    },
    selectLabel:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.count-users-once.count-first-segment`),
    description:
      ({timePeriod}) =>
      ({t}) =>
        t(`query-builder.count-menu.count-users-once.first-segment-tooltip`, {timePeriod}),
    enabledStates: [`enabled`, `required`],
  },
};

/**
 * Template variables for output copies for the selected count type
 * @example: aggregationOperator: total|average|median
 * @example: aggregationOperatorPerUser: total|average|median
 * @example: propertyName: Price|Revenue
 */
export enum CountSelections {
  AggregationOperator = `aggregationOperator`,
  AggregationOperatorPerUser = `aggregationOperatorPerUser`,
  Percentile = `percentile`,
  PropertyName = `propertyName`,
  PropertyValue = `propertyValue`,
  CountUsersOnceType = `countUsersOnceType`,
}

export type CountLabelGeneratorParams = Omit<
  {[key in CountSelections]: string | null | undefined},
  CountSelections.PropertyName | CountSelections.Percentile
> & {
  [CountSelections.PropertyName]: Translatable;
  /** When true, use simplified labels without data group references (for metric-level data group support) */
  withDataGroupSelect?: boolean;

  /** when set, generates count labels for metric names */
  forMetricName?: boolean;
} & {
  [CountSelections.Percentile]?: number;
} & GroupLabelParams;

export type CountLabelGenerator = (params: CountLabelGeneratorParams) => Translatable;

const InsightsCountLabelGeneratorMappings = new Map<InsightsCountType, CountLabelGenerator>([
  [
    InsightsCountType.UniqueUsers,
    ({aggregationOperator, countUsersOnceType, pluralGroupTitle, metricType, withDataGroupSelect, forMetricName}) => {
      const isXau = isXauMathType(aggregationOperator as MATH_TYPE);
      const isCumulativeUnique = PRIMITIVE_MATH_TYPES.CUMULATIVE_UNIQUE === aggregationOperator;

      const operatorLabel = (isXau && aggregationOperator.toUpperCase()) || (isCumulativeUnique && `Cumulative Users`);

      const defaultType: COUNT_USERS_ONCE_TYPE = DefaultCountUsersOnceType.get(metricType as MetricType);
      const countUsersLabel = COUNT_USERS_ONCE_OPTIONS[countUsersOnceType]?.label({defaultType});

      if (forMetricName) {
        // yields a "WAU of Add to Cart" metric name, or "Uniques of Add to Cart"
        return isXau ? operatorLabel : ({t}) => t(`common.uniques`);
      }

      return ({t, print}) => {
        const baseLabel = withDataGroupSelect
          ? t(`common.uniques`)
          : t(`query-builder.count-menu.unique-dg`, {dataGroupName: print(pluralGroupTitle)});

        return [baseLabel, operatorLabel, print(countUsersLabel)].filter(Boolean).join(` - `);
      };
    },
  ],
  [
    InsightsCountType.Total,
    () =>
      ({t}) =>
        t(`query-builder.count-menu.total-events`),
  ],
  [
    InsightsCountType.TotalSessions,
    () =>
      ({t}) =>
        t(`query-builder.count-menu.total-sessions`),
  ],
  [
    InsightsCountType.FrequencyPerUser,
    ({aggregationOperator, percentile, groupTitle, withDataGroupSelect}: CountLabelGeneratorParams) => {
      return ({t, print}) => {
        const dataGroupName = print(groupTitle);
        const printedAggOperator = print(getOperatorLabel(aggregationOperator, percentile));

        if (aggregationOperator === ALL_MATH_TYPES.HISTOGRAM) {
          return withDataGroupSelect
            ? t(`query-builder.count-menu.freq-dist`)
            : t(`query-builder.count-menu.freq-dist-per-group`, {dataGroupName});
        }
        return withDataGroupSelect
          ? t(`query-builder.count-menu.agg-freq`, {aggregationOperator: printedAggOperator})
          : t(`query-builder.count-menu.agg-freq-per-group`, {
              dataGroupName,
              aggregationOperator: printedAggOperator,
            });
      };
    },
  ],
  [
    InsightsCountType.PropertyValues,
    ({aggregationOperator, percentile, propertyName}: CountLabelGeneratorParams) => {
      return ({t, print}) =>
        t(`query-builder.count-menu.agg-of-prop`, {
          propertyName: print(propertyName),
          aggregationOperator: print(getOperatorLabel(aggregationOperator, percentile)),
        });
    },
  ],
  [
    InsightsCountType.PropertyValuePerUser,
    ({
      aggregationOperator,
      aggregationOperatorPerUser,
      percentile,
      propertyName,
      groupTitle,
      withDataGroupSelect,
    }: CountLabelGeneratorParams) => {
      return ({t, print}) => {
        const key = withDataGroupSelect
          ? `query-builder.count-menu.agg-prop-without-group`
          : `query-builder.count-menu.agg-prop-per-group`;

        const params = {
          aggregationOperator: print(getOperatorLabel(aggregationOperator, percentile)),
          propertyName: print(propertyName),
          aggregationOperatorPerUser: print(getOperatorLabel(aggregationOperatorPerUser, percentile)),
          ...(withDataGroupSelect ? {} : {dataGroupName: print(groupTitle)}),
        };

        return t(key, params);
      };
    },
  ],
]);

const RetentionCountLabelGeneratorMappings = new Map<RetentionCountType, CountLabelGenerator>([
  [
    RetentionCountType.Unique,
    ({countUsersOnceType, pluralGroupTitle, metricType, withDataGroupSelect}) => {
      const defaultType: COUNT_USERS_ONCE_TYPE = DefaultCountUsersOnceType.get(metricType as MetricType);
      const countUsersLabel = COUNT_USERS_ONCE_OPTIONS[countUsersOnceType]?.label({defaultType});

      return ({t, print}) => {
        const baseLabel = withDataGroupSelect
          ? t(`common.uniques`)
          : t(`query-builder.count-menu.unique-dg`, {dataGroupName: print(pluralGroupTitle)});

        return [baseLabel, print(countUsersLabel)].filter(Boolean).join(` - `);
      };
    },
  ],
  [
    RetentionCountType.RetentionRate,
    () =>
      ({t}) =>
        t(`query-builder.count-menu.retention-rate`),
  ],
  [
    RetentionCountType.PropertySum,
    ({propertyName}: CountLabelGeneratorParams) => {
      return ({t, print}) =>
        t(`query-builder.count-menu.agg-of-prop`, {
          aggregationOperator: t(`query-builder.count-menu.operators.sum`),
          propertyName: print(propertyName),
        });
    },
  ],
  [
    RetentionCountType.PropertyAverage,
    ({propertyName}: CountLabelGeneratorParams) => {
      return ({t, print}) =>
        t(`query-builder.count-menu.agg-of-prop`, {
          aggregationOperator: t(`query-builder.count-menu.operators.avg`),
          propertyName: print(propertyName),
        });
    },
  ],
]);

const FunnelsCountLabelGeneratorMappings = new Map<FunnelsCountType, CountLabelGenerator>([
  [
    FunnelsCountType.Unique,
    ({countUsersOnceType, pluralGroupTitle, metricType, withDataGroupSelect}) => {
      const defaultType: COUNT_USERS_ONCE_TYPE = DefaultCountUsersOnceType.get(metricType as MetricType);
      const countUsersLabel = COUNT_USERS_ONCE_OPTIONS[countUsersOnceType]?.label({defaultType});

      return ({t, print}) => {
        const baseLabel = withDataGroupSelect
          ? t(`common.uniques`)
          : t(`query-builder.count-menu.unique-dg`, {dataGroupName: print(pluralGroupTitle)});

        return [baseLabel, print(countUsersLabel)].filter(Boolean).join(` - `);
      };
    },
  ],
  [
    FunnelsCountType.Session,
    () =>
      ({t}) =>
        t(`query-builder.count-menu.total-sessions`),
  ],
  [
    FunnelsCountType.ConversionRate,
    ({aggregationOperator}) => {
      if (FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL === aggregationOperator) {
        return ({t}) => t(`query-builder.count-menu.conversion-rate-total`);
      }
      if (FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION === aggregationOperator) {
        return ({t}) => t(`query-builder.count-menu.conversion-rate-sessions`);
      }
      return ({t}) => t(`query-builder.count-menu.conversion-rate`);
    },
  ],
  [
    FunnelsCountType.PropertySum,
    ({propertyName}: CountLabelGeneratorParams) => {
      return ({t, print}) =>
        t(`query-builder.count-menu.agg-of-prop`, {
          aggregationOperator: t(`query-builder.count-menu.operators.sum`),
          propertyName: print(propertyName),
        });
    },
  ],
  [
    FunnelsCountType.General,
    ({aggregationOperator, percentile, propertyName, propertyValue}: CountLabelGeneratorParams) => {
      if (propertyValue === FUNNELS_TIME_TO_CONVERSION.value) {
        if (aggregationOperator in OperatorToString || aggregationOperator in OperatorToStringWithData) {
          return ({t, print}) =>
            t(`query-builder.count-menu.agg-of-prop`, {
              aggregationOperator: print(getOperatorLabel(aggregationOperator, percentile)),
              propertyName: `TTC`,
            });
        }
        return `TTC`;
      } else if (propertyValue !== null && propertyValue !== undefined) {
        return ({t, print}) =>
          t(`query-builder.count-menu.agg-of-prop`, {
            aggregationOperator: print(getOperatorLabel(aggregationOperator, percentile)),
            propertyName: print(propertyName),
          });
      }
      return ({t}) => t(`query-builder.count-menu.total-conversions`);
    },
  ],
]);

const InsightsProfileCountLabelGeneratorMappings = new Map<InsightsCountType, CountLabelGenerator>([
  [
    InsightsCountType.Total,
    () =>
      ({t}) =>
        t(`query-builder.count-menu.total-profiles`),
  ],
  [
    InsightsCountType.UniqueUsers,
    () =>
      ({t}) =>
        t(`query-builder.count-menu.total-profiles`),
  ],
  [
    InsightsCountType.PropertyValues,
    ({aggregationOperator, propertyName}: CountLabelGeneratorParams) => {
      return ({t, print}) =>
        t(`query-builder.count-menu.agg-of-prop`, {
          aggregationOperator: print(OperatorToString[aggregationOperator]),
          propertyName: print(propertyName),
        });
    },
  ],
]);

export const CountLabelGeneratorMappings = new Map<MetricType, Map<CountType, CountLabelGenerator>>([
  [MetricType.Event, InsightsCountLabelGeneratorMappings],
  [MetricType.Simple, InsightsCountLabelGeneratorMappings],
  [MetricType.CustomEvent, InsightsCountLabelGeneratorMappings],
  [MetricType.People, InsightsProfileCountLabelGeneratorMappings],
  [MetricType.Cohort, InsightsProfileCountLabelGeneratorMappings],
  [MetricType.Funnel, FunnelsCountLabelGeneratorMappings],
  [MetricType.Retention, RetentionCountLabelGeneratorMappings],
  [MetricType.RetentionFrequency, RetentionCountLabelGeneratorMappings],
]);
